import { createContext, useContext, useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { api_url } from "../config/config";
import { clearCartPrescriptions } from "../utils/cartPrescriptionStorage";

const CartContext = createContext();

// Exported so any page that keeps its own local copy of the cart (e.g. the
// cart page itself) can listen for the same cross-tab signal.
export const CART_SYNC_KEY = "cart_sync";

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showDisable, setShowDisable] = useState(false);

  const isToken = localStorage.getItem("authToken");

  // Cross-tab sync: each tab's cart state is its own React state fetched
  // once on mount, so placing an order (or adding/removing an item) in one
  // tab never reached any other open tab — leaving a stale cart there that
  // could still be checked out again. Writing to this localStorage key
  // fires the "storage" event in every OTHER open tab (never the tab that
  // wrote it), which they use below to re-fetch the real cart from the
  // server.
  const broadcastCartUpdate = () => {
    try {
      localStorage.setItem(CART_SYNC_KEY, String(Date.now()));
    } catch {}
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CART_SYNC_KEY) {
        // Every current broadcaster only fires once the cart is already
        // empty (order placed or Clear Cart), so zero the badge out
        // immediately instead of waiting on the round trip below — then
        // still fetch to reconcile with the server just in case.
        setCartCount(0);
        setCartItems([]);
        fetchCartCount();
        fetchCartProducts();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Fetch cart count ── */
  const fetchCartCount = async () => {
    if (!isToken) return;
    try {
      const response = await axios.get(`${api_url}/count-cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setCartCount(response.data.count);
    } catch (error) {
      console.error("Error fetching cart count:", error);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, []);

  /* ── Fetch cart products ── */
  const fetchCartProducts = async () => {
    try {
      const response = await axios.get(`${api_url}/cart`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      const data = response?.data?.data?.cartproduct || [];
      setCartItems(data);
    } catch (error) {
      console.error("Error fetching cart products:", error);
    }
  };

  useEffect(() => {
    fetchCartProducts();
  }, []);

  /* ── Add to Cart ── */
  const handleAddToCart = async (product, selectedVariantId = null) => {
    if (!isToken) {
      setShowLoginPopup(true);
      return;
    }

    const variantId = (
      selectedVariantId?.variant_id ||
      product.variant_id ||
      (Array.isArray(product.variant) && product.variant.length > 0
        ? product.variant[0]?.variant_id
        : null)
    )?.toString();

    if (!variantId || typeof variantId !== "string") {
      toast.error("Please select a valid variant before adding to cart.");
      return;
    }

    const payload = {
      product_id: product.product_id,
      variant_id: variantId,
      quantity: 1,
    };

    try {
      const response = await fetch(`${api_url}/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setShowDisable(false);
        toast.success("Product added to cart!", {
          position: "bottom-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });

        setCartItems((prevCart) => {
          const existingIndex = prevCart.findIndex(
            (item) =>
              item.product_id === product.product_id &&
              item.variant_id === variantId,
          );
          if (existingIndex !== -1) {
            const updated = [...prevCart];
            updated[existingIndex].quantity += 1;
            return updated;
          }
          return [
            ...prevCart,
            { ...product, variant_id: variantId, quantity: 1 },
          ];
        });

        // Re-fetch the authoritative count instead of guessing +1 locally —
        // a naive local increment drifts from the backend's real count
        // whenever the item was already in the cart (bumping an existing
        // line's quantity rather than adding a new one), which is exactly
        // what showed up as the badge disagreeing between pages.
        fetchCartCount();
        broadcastCartUpdate();
      } else {
        setShowDisable(true);
        toast.error(data.message || "Failed to add product to cart.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Failed to add product to cart:", error);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  /* ── Remove from Cart ── */
  const handleRemoveFromCart = async (productId, variant_id) => {
    try {
      const response = await fetch(`${api_url}/cart`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({ product_id: productId, variant_id }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Product removed from cart.", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: true,
        });

        setCartItems((prev) =>
          prev.filter(
            (item) =>
              !(
                item.product_id === productId &&
                (item.variant_id === variant_id || !variant_id)
              ),
          ),
        );

        // Re-fetch the authoritative count rather than guessing -1 locally
        // (see handleAddToCart for why the naive version drifts).
        fetchCartCount();
        broadcastCartUpdate();
      } else {
        toast.error(data.message || "Failed to remove product.", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to remove product from cart:", err);
      toast.error("Something went wrong. Please try again.", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  /* ── Logout ── */
  const handleLogout = () => {
    setCartItems([]);
    setCartCount(0);
    clearCartPrescriptions();
    localStorage.removeItem("authToken");
    localStorage.removeItem("cart");
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        fetchCartProducts,
        setCartItems,
        handleAddToCart,
        handleRemoveFromCart,
        setCartCount,
        cartCount,
        fetchCartCount,
        handleLogout,
        showLoginPopup,
        setShowLoginPopup,
        showDisable,
        broadcastCartUpdate,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
